import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent } from '../ui/card'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { TabProps } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { CalendarClock, Package, Layers, ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { purchasingAPI, POLineSchedule, PartSchedule } from '../../lib/api'

/**
 * FutureForecastTab — Firm vs Forecast segmentation for upcoming PO schedules.
 *
 * Pulls PartSchedule rows once to derive a per-part firm-window map
 * (today + schedule_firm days). For each PO line schedule, classifies
 * `schedule_date` as FIRM (within the window) or FORECAST (beyond it).
 * Parts with no PartSchedule row default to FIRM (legacy convention).
 */
export function FutureForecastTab({ data: _data, currency: _currency, filters: _filters }: TabProps) {
  const { formatNumber } = useFormatters()
  const [schedules, setSchedules] = useState<POLineSchedule[]>([])
  const [partSchedules, setPartSchedules] = useState<PartSchedule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAllFirm, setShowAllFirm] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const [schedRes, partRes] = await Promise.all([
          purchasingAPI.getSchedules({ page_size: 500, status: 'scheduled' }),
          purchasingAPI.getPartSchedules({ page_size: 500, active: true }),
        ])
        setSchedules(schedRes.data.results || [])
        setPartSchedules(partRes.data.results || [])
      } catch (err: unknown) {
        console.error('Error loading future forecast data:', err)
        const message = err instanceof Error ? err.message : 'Failed to load forecast data'
        setError(message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // Build part_number -> firm_days map from PartSchedule list
  const firmDaysByPart = useMemo(() => {
    const map = new Map<string, number>()
    partSchedules.forEach((p) => {
      if (p.active && typeof p.schedule_firm === 'number') {
        map.set(p.part_number, p.schedule_firm)
      }
    })
    return map
  }, [partSchedules])

  // Classify each schedule and attach metadata
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  type Segment = 'firm' | 'forecast' | 'no_config'
  interface Classified {
    sched: POLineSchedule
    segment: Segment
    qty: number
    date: Date
  }

  const classified = useMemo<Classified[]>(() => {
    return schedules
      .map((s) => {
        const date = new Date(s.schedule_date)
        const qty = parseFloat(s.balance_qty || s.schedule_qty || '0') || 0
        const firmDays = firmDaysByPart.get(s.part_number)
        let segment: Segment
        if (firmDays === undefined) {
          segment = 'no_config'
        } else {
          const boundary = new Date(today)
          boundary.setDate(boundary.getDate() + firmDays)
          segment = date <= boundary ? 'firm' : 'forecast'
        }
        return { sched: s, segment, qty, date }
      })
      .filter((r) => !isNaN(r.date.getTime()) && r.date >= today)
  }, [schedules, firmDaysByPart, today])

  // KPIs — treat no_config as firm per legacy rule
  const kpis = useMemo(() => {
    const firmRows = classified.filter((r) => r.segment === 'firm' || r.segment === 'no_config')
    const forecastRows = classified.filter((r) => r.segment === 'forecast')
    const sumQty = (rows: Classified[]) => rows.reduce((acc, r) => acc + r.qty, 0)
    return {
      firmCount: firmRows.length,
      firmQty: sumQty(firmRows),
      forecastCount: forecastRows.length,
      forecastQty: sumQty(forecastRows),
      totalCount: classified.length,
      totalQty: sumQty(classified),
    }
  }, [classified])

  // Next 8 weeks chart data (week of year buckets)
  const chartData = useMemo(() => {
    const buckets: { key: string; label: string; firm: number; forecast: number; start: Date }[] = []
    const startOfWeek = new Date(today)
    const day = startOfWeek.getDay()
    const diff = (day === 0 ? -6 : 1) - day // Monday as week start
    startOfWeek.setDate(startOfWeek.getDate() + diff)
    for (let i = 0; i < 8; i++) {
      const start = new Date(startOfWeek)
      start.setDate(start.getDate() + i * 7)
      const label = start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
      buckets.push({ key: `w${i}`, label, firm: 0, forecast: 0, start })
    }
    classified.forEach((r) => {
      const idx = Math.floor((r.date.getTime() - startOfWeek.getTime()) / (7 * 24 * 60 * 60 * 1000))
      if (idx >= 0 && idx < 8) {
        if (r.segment === 'forecast') buckets[idx].forecast += r.qty
        else buckets[idx].firm += r.qty
      }
    })
    return buckets
  }, [classified, today])

  // Next firm schedules table (sorted by date)
  const upcomingFirm = useMemo(() => {
    return classified
      .filter((r) => r.segment === 'firm' || r.segment === 'no_config')
      .sort((a, b) => a.date.getTime() - b.date.getTime())
  }, [classified])

  const visibleFirm = showAllFirm ? upcomingFirm : upcomingFirm.slice(0, 20)

  const fmtDate = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2 text-sm text-muted-foreground">Loading forecast data...</span>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <p className="text-sm text-red-600">{error}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold">Future Forecast</h2>
        <p className="text-sm text-muted-foreground">
          Firm vs forecast segmentation of upcoming PO schedules, derived per-part from the schedule firm window.
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="shadow-sm border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Firm schedules</p>
                <p className="text-2xl font-bold text-green-700">{kpis.firmCount.toLocaleString('en-GB')}</p>
                <p className="text-xs text-muted-foreground mt-1">{formatNumber(kpis.firmQty)} units</p>
              </div>
              <CalendarClock className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-l-4 border-l-orange-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Forecast (beyond firm)</p>
                <p className="text-2xl font-bold text-orange-700">{kpis.forecastCount.toLocaleString('en-GB')}</p>
                <p className="text-xs text-muted-foreground mt-1">{formatNumber(kpis.forecastQty)} units</p>
              </div>
              <Layers className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-l-4 border-l-slate-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total schedules</p>
                <p className="text-2xl font-bold">{kpis.totalCount.toLocaleString('en-GB')}</p>
                <p className="text-xs text-muted-foreground mt-1">{formatNumber(kpis.totalQty)} units</p>
              </div>
              <Package className="h-8 w-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Next 8 weeks chart */}
      <ChartWrapper
        title="Next 8 weeks — Firm vs Forecast"
        data={chartData}
        exportFilename="future-forecast-8weeks"
        legend={[
          { color: '#22c55e', label: 'Firm' },
          { color: '#f97316', label: 'Forecast' },
        ]}
      >
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value: number) => value.toLocaleString('en-GB')} />
            <Legend />
            <Bar dataKey="firm" stackId="a" fill="#22c55e" name="Firm" radius={[0, 0, 0, 0]} />
            <Bar dataKey="forecast" stackId="a" fill="#f97316" name="Forecast" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Upcoming firm schedules table */}
      <Card className="shadow-sm">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold">Upcoming firm schedules</h3>
              <p className="text-xs text-muted-foreground">
                {upcomingFirm.length} firm schedule{upcomingFirm.length === 1 ? '' : 's'} ahead
                {upcomingFirm.length > 20 && !showAllFirm && ' — showing first 20'}
              </p>
            </div>
            {upcomingFirm.length > 20 && (
              <Button variant="outline" size="sm" onClick={() => setShowAllFirm((v) => !v)}>
                {showAllFirm ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" /> Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" /> Show all
                  </>
                )}
              </Button>
            )}
          </div>
          {visibleFirm.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No upcoming firm schedules</p>
          ) : (
            <div className="overflow-x-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-700">Date</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-700">PO #</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-700">Part</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-700">Qty</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-700">Supplier</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-700">Segment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleFirm.map((r) => (
                    <tr key={r.sched.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.sched.po_number}</td>
                      <td className="px-3 py-2">{r.sched.part_number}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.qty.toLocaleString('en-GB')}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.sched.supplier_name || '-'}</td>
                      <td className="px-3 py-2">
                        {r.segment === 'no_config' ? (
                          <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300">NO CONFIG</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">FIRM</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
